/**
 * Transaction processing utilities
 * Functions for processing and sorting Firestore transaction documents
 * Extracted from Activity.tsx to improve code organization
 */

import { DocumentSnapshot, QuerySnapshot, Timestamp } from "firebase/firestore";
import { Transaction, AttachmentHistoryEntry, TransactionDetailValue, TransactionCustomFieldValues } from "../types";

/**
 * Extended Transaction type with Date instead of Timestamp
 */
export interface TransactionWithDate extends Omit<Transaction, "date"> {
  date: Date;
  currency?: string;
  rate?: number;
  updatedAt?: Timestamp | Date | number | { seconds: number; nanoseconds?: number };
}

/**
 * Parse a raw date value (from API/Firestore) into a Date.
 * Handles Timestamp, Date, number, { seconds } / { _seconds }, and string.
 */
export function parseTransactionDateFromRaw(dateRaw: unknown): Date {
  if (dateRaw == null) return new Date();
  if (typeof (dateRaw as Timestamp).toDate === "function") {
    return (dateRaw as Timestamp).toDate();
  }
  if (dateRaw instanceof Date) return dateRaw;
  if (typeof dateRaw === "number") return new Date(dateRaw);
  if (typeof dateRaw === "object") {
    const obj = dateRaw as Record<string, unknown>;
    const seconds = (obj.seconds ?? obj._seconds) as number | undefined;
    if (typeof seconds === "number") return new Date(seconds * 1000);
  }
  if (typeof dateRaw === "string") return new Date(dateRaw);
  console.warn("Invalid date format for transaction:", dateRaw);
  return new Date();
}

/**
 * Process a Firestore document snapshot into a TransactionWithDate
 * Handles various date formats from Firestore
 */
export const processTransaction = (docSnap: DocumentSnapshot): TransactionWithDate => {
  const data = docSnap.data();
  if (!data) {
    throw new Error("Transaction data is missing");
  }

  const dateValue = parseTransactionDateFromRaw(data.date);
  
  const transactionType = (data.type as "income" | "expense" | "transfer" | "adjustment") || (data.isIncome ? "income" : "expense");
  // Use snapshot id first; fallback to data.id for docs written with id in payload (e.g. new fee on edit)
  const transactionId = docSnap.id || (data as Record<string, unknown>).id as string | undefined;
  const transaction: TransactionWithDate = {
    id: transactionId ?? "",
    userId: data.userId as string,
    type: transactionType,
    amount: data.amount as number,
    currency: data.currency as string | undefined,
    date: dateValue,
    note: data.note as string | undefined,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt as Timestamp | Date | number | { seconds: number; nanoseconds?: number } | undefined,
    // Expense/Income fields
    title: data.title as string | undefined,
    category: data.category as string | undefined,
    isIncome: data.isIncome as boolean | undefined,
    // Transfer fields
    fromAccountId: data.fromAccountId as string | undefined,
    fromAccountName: data.fromAccountName as string | undefined,
    toAccountId: data.toAccountId as string | undefined,
    toAccountName: data.toAccountName as string | undefined,
    transferRate: data.transferRate as number | undefined,
    // Account field for expense/income/adjustments
    accountId: data.accountId as string | undefined,
    accountName: data.accountName as string | undefined,
    rate: data.rate as number | undefined, // Exchange rate for expense/income currency conversion
    defaultCurrencyRate: data.defaultCurrencyRate as number | undefined,
    amountInDefaultCurrency: data.amountInDefaultCurrency as number | undefined,
    defaultCurrencyAtTime: data.defaultCurrencyAtTime as string | undefined,
    // Adjustment fields
    previousAmount: data.previousAmount as number | undefined,
    isInitialBalance: data.isInitialBalance as boolean | undefined,
    // Attachment fields
    attachmentUrl: data.attachmentUrl as string | undefined,
    attachmentName: data.attachmentName as string | undefined,
    attachmentType: data.attachmentType as string | undefined,
    attachmentHistory: data.attachmentHistory as AttachmentHistoryEntry[] | undefined,
    // Details field
    details: data.details as TransactionDetailValue | undefined,
    // Custom field values (workspace-configured)
    customFieldValues: data.customFieldValues as TransactionCustomFieldValues | undefined,
    // Fee transaction link
    transferFeeFor: data.transferFeeFor as string | undefined,
    expenseFeeFor: data.expenseFeeFor as string | undefined,
    // User-friendly ID sequence (display = prefix + "-" + sequence from workspace settings)
    friendlyIdSequence: data.friendlyIdSequence as number | undefined,
  };
  
  // Debug: Log attachment data if present
  if (data.attachmentUrl) {
    console.log("Transaction attachment found:", {
      id: docSnap.id,
      url: data.attachmentUrl,
      name: data.attachmentName,
      type: data.attachmentType,
    });
  }
  
  return transaction;
};

/**
 * Sort transactions by date (descending - newest first)
 * Within the same day, transactions with time come before those without
 * For transactions without time on the same date, newer ones (by createdAt) appear first
 */
export const sortTransactionsByDate = (transactions: TransactionWithDate[]): TransactionWithDate[] => {
  return [...transactions].sort((a, b) => {
    if (!a.date || !b.date) {
      // If either date is missing, put it at the end
      if (!a.date && !b.date) return 0;
      return !a.date ? 1 : -1;
    }
    
    // Get date part only (without time) for day comparison
    const dateA = new Date(a.date.getFullYear(), a.date.getMonth(), a.date.getDate());
    const dateB = new Date(b.date.getFullYear(), b.date.getMonth(), b.date.getDate());
    
    // First, compare by date (descending - newest first)
    const dateDiff = dateB.getTime() - dateA.getTime();
    if (dateDiff !== 0) {
      return dateDiff;
    }
    
    // Same day - now sort by time or createdAt
    // Check if transactions have time (not midnight 00:00:00)
    const hasTimeA = !(a.date.getHours() === 0 && a.date.getMinutes() === 0 && a.date.getSeconds() === 0);
    const hasTimeB = !(b.date.getHours() === 0 && b.date.getMinutes() === 0 && b.date.getSeconds() === 0);
    
    // If one has time and one doesn't, put the one without time at the bottom
    if (hasTimeA && !hasTimeB) return -1; // A has time, B doesn't - A comes first
    if (!hasTimeA && hasTimeB) return 1;  // A doesn't have time, B does - B comes first
    
    // Both have time - sort by time (HH:MM) descending - newest time first
    if (hasTimeA && hasTimeB) {
      // Compare by time of day (hours and minutes)
      const timeA = a.date.getHours() * 60 + a.date.getMinutes();
      const timeB = b.date.getHours() * 60 + b.date.getMinutes();
      // If times are equal, compare seconds
      if (timeA === timeB) {
        const secondsDiff = b.date.getSeconds() - a.date.getSeconds();
        // If seconds are also equal, use updatedAt/createdAt as tiebreaker
        if (secondsDiff === 0) {
          const getSortTime = (transaction: TransactionWithDate): number => {
            const timestamp = transaction.updatedAt || transaction.createdAt;
            if (!timestamp) return -1;
            try {
              if (typeof (timestamp as Timestamp).toDate === "function") {
                return (timestamp as Timestamp).toDate().getTime();
              }
              if (timestamp instanceof Date) {
                return timestamp.getTime();
              }
              if (typeof timestamp === "number") {
                return timestamp;
              }
              if (timestamp && typeof timestamp === "object" && "seconds" in timestamp) {
                const timestampObj = timestamp as { seconds: number; nanoseconds?: number };
                const seconds = timestampObj.seconds || 0;
                const nanoseconds = timestampObj.nanoseconds || 0;
                return seconds * 1000 + nanoseconds / 1000000;
              }
            } catch (error) {
              console.warn("Error parsing timestamp for transaction:", transaction.id, error);
            }
            return -1;
          };
          const sortTimeA = getSortTime(a);
          const sortTimeB = getSortTime(b);
          return sortTimeB - sortTimeA; // Descending - newer first
        }
        return secondsDiff;
      }
      return timeB - timeA; // Descending - newest time first
    }
    
    // Both don't have time - sort by updatedAt (if edited) or createdAt descending - newest first
    // This ensures edited transactions move up, and new transactions without time appear above existing ones without time on the same date
    if (!hasTimeA && !hasTimeB) {
      const getSortTime = (transaction: TransactionWithDate): number => {
        // Prefer updatedAt if it exists (transaction was edited), otherwise use createdAt
        const timestamp = transaction.updatedAt || transaction.createdAt;
        if (!timestamp) return -1; // Missing timestamp goes to bottom
        
        try {
          // Handle Firestore Timestamp
          if (typeof (timestamp as Timestamp).toDate === "function") {
            return (timestamp as Timestamp).toDate().getTime();
          }
          // Handle Date object
          if (timestamp instanceof Date) {
            return timestamp.getTime();
          }
          // Handle timestamp number (milliseconds)
          if (typeof timestamp === "number") {
            return timestamp;
          }
          // Handle timestamp object with seconds (Firestore format)
          if (timestamp && typeof timestamp === "object" && "seconds" in timestamp) {
            const timestampObj = timestamp as { seconds: number; nanoseconds?: number };
            const seconds = timestampObj.seconds || 0;
            const nanoseconds = timestampObj.nanoseconds || 0;
            return seconds * 1000 + nanoseconds / 1000000; // Convert to milliseconds
          }
        } catch (error) {
          console.warn("Error parsing timestamp for transaction:", transaction.id, error);
        }
        return -1; // Invalid timestamp goes to bottom
      };
      
      const sortTimeA = getSortTime(a);
      const sortTimeB = getSortTime(b);
      return sortTimeB - sortTimeA; // Descending - newer first
    }
    
    // Fallback (shouldn't reach here)
    return 0;
  });
};

/**
 * Process a QuerySnapshot into an array of sorted TransactionWithDate
 */
export const processTransactionSnapshot = (snapshot: QuerySnapshot): TransactionWithDate[] => {
  console.log("Transaction snapshot received:", snapshot.size, "transactions");
  
  const transactionsList: TransactionWithDate[] = snapshot.docs.map((docSnap) => {
    try {
      const transaction = processTransaction(docSnap);
      return transaction;
    } catch (error) {
      console.error("Error processing transaction:", docSnap.id, error);
      return null;
    }
  }).filter((t): t is TransactionWithDate => t !== null);
  
  // Sort by date descending
  return sortTransactionsByDate(transactionsList);
};
